import { useRef } from "react";
import type { CSSProperties } from "react";

/**
 * SG-1..SG-5: przelacznik segmentowy. 2-5 wzajemnie wykluczajacych sie OPCJI
 * (nie akcji - te sa przyciskami).
 *
 * M-1: aktywna opcje wskazuje JEDEN wspolny wskaznik, ktory sie PRZESUWA. Nie
 * przelaczamy N tel gasnacych i zapalajacych sie - zmienia sie jedna liczba
 * (--active), a wskaznik sam dojezdza. Wartosci 1:1 z segmented-control.html.
 *
 * DWA odstepstwa od gotowca:
 * 1. Prog pionowy to 600px, a nie 640px. BP-1 dopuszcza w calej aplikacji dokladnie
 *    dwa progi (600 i 1024); 640 z gotowca jest trzecim i lamie regule.
 * 2. Roving tabindex: tylko aktywny segment jest w kolejnosci Taba, po reszcie chodzi
 *    sie strzalkami. Gotowiec zostawia wszystkie segmenty tabowalne, przez co Tab
 *    przeklikuje sie przez caly przelacznik zamiast go przeskoczyc (A-4).
 */

export interface OpcjaSegmentu<T extends string = string> {
  wartosc: T;
  etykieta: string;
}

export function SegmentedControl<T extends string>({
  etykieta,
  opcje,
  wartosc,
  onZmiana,
}: {
  /** A-5: nazwa grupy dla czytnika ekranu. */
  etykieta: string;
  opcje: readonly OpcjaSegmentu<T>[];
  wartosc: T;
  onZmiana: (wartosc: T) => void;
}): React.JSX.Element {
  const korzenRef = useRef<HTMLDivElement>(null);
  const aktywny = Math.max(
    0,
    opcje.findIndex((o) => o.wartosc === wartosc),
  );

  function ustaw(i: number): void {
    const opcja = opcje[i];
    if (!opcja) return;
    onZmiana(opcja.wartosc);
    korzenRef.current?.querySelectorAll<HTMLButtonElement>(".seg__btn")[i]?.focus();
  }

  function naKlawisz(e: React.KeyboardEvent<HTMLButtonElement>, i: number): void {
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      ustaw((i + 1) % opcje.length);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      ustaw((i - 1 + opcje.length) % opcje.length);
    } else if (e.key === "Home") {
      e.preventDefault();
      ustaw(0);
    } else if (e.key === "End") {
      e.preventDefault();
      ustaw(opcje.length - 1);
    }
  }

  const zmienne = { "--count": opcje.length, "--active": aktywny } as CSSProperties;

  return (
    <div className="seg" role="tablist" aria-label={etykieta} style={zmienne} ref={korzenRef}>
      <div className="seg__indicator" aria-hidden="true" />
      {opcje.map((opcja, i) => (
        <button
          key={opcja.wartosc}
          type="button"
          className="seg__btn"
          role="tab"
          aria-selected={i === aktywny}
          tabIndex={i === aktywny ? 0 : -1}
          onClick={() => ustaw(i)}
          onKeyDown={(e) => naKlawisz(e, i)}
        >
          {opcja.etykieta}
        </button>
      ))}
    </div>
  );
}
